import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight } from "lucide-react";
import React, { useSyncExternalStore, useState } from "react";
import { cn } from "@/lib/utils";
interface InteractiveHoverButtonProps {
  children?: React.ReactNode;
  text?: string;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  icon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
}
const subscribe = (callback: () => void) => {
  if (typeof document === "undefined") return () => {};
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"]
  });
  return () => observer.disconnect();
};
const getSnapshot = () => {
  if (typeof document === "undefined") return "brutalist";
  const cl = document.documentElement.classList;
  if (cl.contains("theme-neumorphic")) return "neumorphic";
  if (cl.contains("theme-color")) return "ember";
  return "brutalist";
};
const getServerSnapshot = () => "brutalist";
export function InteractiveHoverButton(t0) {
  const $ = _c(60);
  let children;
  let className;
  let href;
  let icon;
  let props;
  let rel;
  let target;
  let text;
  if ($[0] !== t0) {
    ({
      children,
      className,
      text,
      href,
      target,
      rel,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = icon;
    $[5] = props;
    $[6] = rel;
    $[7] = target;
    $[8] = text;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    icon = $[4];
    props = $[5];
    rel = $[6];
    target = $[7];
    text = $[8];
  }
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const content = text || children;
  const [isHovered, setIsHovered] = useState(false);
  let baseClass;
  let dotClass;
  let textClass;
  let arrowClass;
  let borderRadiusClass;
  let dotRoundingClass;
  if (theme === "neumorphic") {
    baseClass = "group bg-[#e0e5ec] text-[#4b5563] border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] data-[hover=true]:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]";
    dotClass = "bg-[#4b5563]";
    textClass = "text-[#4b5563] group-data-[hover=true]:text-[#e0e5ec] group-active:text-[#e0e5ec] group-active:transition-none";
    arrowClass = "text-[#e0e5ec]";
    borderRadiusClass = "rounded-full";
    dotRoundingClass = "rounded-full";
  } else {
    if (theme === "ember") {
      baseClass = "group bg-transparent text-[#1A1716] border-4 border-[#1A1716] data-[hover=true]:bg-[#1A1716] data-[hover=true]:text-[#FF4F00] data-[hover=true]:border-[#1A1716] active:bg-[#1A1716] active:text-[#FF4F00] active:border-[#1A1716]";
      dotClass = "bg-[#1A1716]";
      textClass = "text-[#1A1716] group-data-[hover=true]:text-[#FF4F00] group-active:text-[#FF4F00] group-active:transition-none";
      arrowClass = "text-[#FF4F00]";
      borderRadiusClass = "rounded-none";
      dotRoundingClass = "rounded-none";
    } else {
      baseClass = "group bg-transparent text-white border-4 border-white data-[hover=true]:bg-white data-[hover=true]:text-black data-[hover=true]:border-white active:bg-white active:text-black active:border-white";
      dotClass = "bg-white";
      textClass = "text-white group-data-[hover=true]:text-black group-active:text-black group-active:transition-none";
      arrowClass = "text-black";
      borderRadiusClass = "rounded-none";
      dotRoundingClass = "rounded-none";
    }
  }
  let commonClass;
  let innerContent;
  let t1;
  if ($[9] !== arrowClass || $[10] !== baseClass || $[11] !== borderRadiusClass || $[12] !== className || $[13] !== content || $[14] !== dotClass || $[15] !== dotRoundingClass || $[16] !== href || $[17] !== icon || $[18] !== isHovered || $[19] !== props || $[20] !== rel || $[21] !== target || $[22] !== textClass) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      commonClass = cn("relative w-full sm:w-48 cursor-pointer overflow-hidden p-3 px-6 text-center text-xl font-black uppercase transition-all duration-300 ease-in-out active:scale-[0.98] [transform:translate3d(0,0,0)] [backface-visibility:hidden] will-change-transform", borderRadiusClass, baseClass, className);
      let t2;
      if ($[26] !== dotClass || $[27] !== dotRoundingClass || $[28] !== icon || $[29] !== textClass) {
        t2 = icon ? <div className="relative flex items-center justify-center shrink-0 z-10"><div className={cn("absolute inset-0 m-auto h-2.5 w-2.5 transition-transform duration-300 ease-in-out scale-0 group-data-[hover=true]:scale-[100.8] group-data-[hover=true]:duration-[900ms] group-data-[hover=true]:ease-out origin-center shrink-0 z-[-1] will-change-transform [transform:translate3d(0,0,0)]", dotRoundingClass, dotClass)} /><span className={cn("relative z-10 flex items-center justify-center w-5 h-5 transition-colors duration-300", textClass)}>{icon}</span></div> : <div className={cn("relative z-0 h-2.5 w-2.5 transition-transform duration-300 ease-in-out group-data-[hover=true]:scale-[100.8] group-data-[hover=true]:duration-[900ms] group-data-[hover=true]:ease-out origin-center shrink-0 will-change-transform [transform:translate3d(0,0,0)]", dotRoundingClass, dotClass)} />;
        $[26] = dotClass;
        $[27] = dotRoundingClass;
        $[28] = icon;
        $[29] = textClass;
        $[30] = t2;
      } else {
        t2 = $[30];
      }
      let t3;
      if ($[31] !== textClass) {
        t3 = cn("relative z-10 transition-colors duration-300 ease-in-out group-data-[hover=true]:duration-[450ms] select-none whitespace-nowrap [transform:translate3d(0,0,0)]", textClass);
        $[31] = textClass;
        $[32] = t3;
      } else {
        t3 = $[32];
      }
      let t4;
      if ($[33] !== content || $[34] !== t3) {
        t4 = <span className={t3}>{content}</span>;
        $[33] = content;
        $[34] = t3;
        $[35] = t4;
      } else {
        t4 = $[35];
      }
      let t5;
      if ($[36] !== arrowClass) {
        t5 = cn("absolute left-full ml-2 z-10 flex items-center transition-all duration-[450ms] ease-in-out group-data-[hover=true]:duration-[650ms] group-data-[hover=true]:ease-out opacity-0 -translate-x-2 group-data-[hover=true]:translate-x-0 group-data-[hover=true]:opacity-100 shrink-0 [transform:translate3d(0,0,0)]", arrowClass);
        $[36] = arrowClass;
        $[37] = t5;
      } else {
        t5 = $[37];
      }
      let t6;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <ArrowUpRight className="w-6 h-6 transition-transform duration-300 group-data-[hover=true]:translate-x-0.5 group-data-[hover=true]:-translate-y-0.5" />;
        $[38] = t6;
      } else {
        t6 = $[38];
      }
      let t7;
      if ($[39] !== t5) {
        t7 = <div className={t5}>{t6}</div>;
        $[39] = t5;
        $[40] = t7;
      } else {
        t7 = $[40];
      }
      let t8;
      if ($[41] !== t2 || $[42] !== t4 || $[43] !== t7) {
        t8 = <div className="relative z-10 flex items-center justify-center w-full h-full"><div className="flex items-center gap-2.5 relative transition-transform duration-[450ms] ease-in-out group-data-[hover=true]:-translate-x-3 will-change-transform [transform:translate3d(0,0,0)]">{t2}{t4}{t7}</div></div>;
        $[41] = t2;
        $[42] = t4;
        $[43] = t7;
        $[44] = t8;
      } else {
        t8 = $[44];
      }
      innerContent = t8;
      if (href) {
        let t10;
        let t11;
        let t12;
        let t13;
        let t9;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = () => setIsHovered(true);
          t10 = () => setIsHovered(false);
          t11 = () => setIsHovered(true);
          t12 = () => setIsHovered(false);
          t13 = () => setIsHovered(false);
          $[45] = t10;
          $[46] = t11;
          $[47] = t12;
          $[48] = t13;
          $[49] = t9;
        } else {
          t10 = $[45];
          t11 = $[46];
          t12 = $[47];
          t13 = $[48];
          t9 = $[49];
        }
        t1 = <a href={href} target={target} rel={rel} className={cn("inline-block", commonClass)} data-hover={isHovered} onMouseEnter={t9} onMouseLeave={t10} onTouchStart={t11} onTouchEnd={t12} onTouchCancel={t13} {...props as Omit<React.ComponentPropsWithoutRef<"a">, keyof InteractiveHoverButtonProps>}>{innerContent}</a>;
        break bb0;
      }
    }
    $[9] = arrowClass;
    $[10] = baseClass;
    $[11] = borderRadiusClass;
    $[12] = className;
    $[13] = content;
    $[14] = dotClass;
    $[15] = dotRoundingClass;
    $[16] = href;
    $[17] = icon;
    $[18] = isHovered;
    $[19] = props;
    $[20] = rel;
    $[21] = target;
    $[22] = textClass;
    $[23] = commonClass;
    $[24] = innerContent;
    $[25] = t1;
  } else {
    commonClass = $[23];
    innerContent = $[24];
    t1 = $[25];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsHovered(true);
    t3 = () => setIsHovered(false);
    t4 = () => setIsHovered(true);
    t5 = () => setIsHovered(false);
    t6 = () => setIsHovered(false);
    $[50] = t2;
    $[51] = t3;
    $[52] = t4;
    $[53] = t5;
    $[54] = t6;
  } else {
    t2 = $[50];
    t3 = $[51];
    t4 = $[52];
    t5 = $[53];
    t6 = $[54];
  }
  const t7 = props as Omit<React.ComponentPropsWithoutRef<"button">, keyof InteractiveHoverButtonProps>;
  let t8;
  if ($[55] !== commonClass || $[56] !== innerContent || $[57] !== isHovered || $[58] !== t7) {
    t8 = <button className={commonClass} data-hover={isHovered} onMouseEnter={t2} onMouseLeave={t3} onTouchStart={t4} onTouchEnd={t5} onTouchCancel={t6} {...t7}>{innerContent}</button>;
    $[55] = commonClass;
    $[56] = innerContent;
    $[57] = isHovered;
    $[58] = t7;
    $[59] = t8;
  } else {
    t8 = $[59];
  }
  return t8;
}